import { trans } from "@mongez/localization";
import { useMemo, useState } from "react";

import { Form } from "@mongez/react-form";
import { orderAtom, useCancelOrder } from "core/orders";
import { createPortal } from "core/shared";
import {
  Button,
  errorToast,
  RadioGroup,
  RadioInput,
  SubmitButton,
  successToast,
  TextareaInput,
} from "~/design-system/components";
import { Portal } from "~/modules/shared/components/Portal";

export const cancelOrderPortal = createPortal<{
  cancelingReasons: any[];
  orderId: number;
}>("cancel-order");

export function CancelOrderPortal() {
  const { cancelingReasons = [], orderId } = cancelOrderPortal.useData();

  const { submitCancelingOrder, setSelectedCancelingReason } = useCancelOrder(
    orderId,
    {
      onSuccess: (data: any) => {
        successToast(trans("orderCanceledSuccessfully"));
        orderAtom.update(data.order);
        cancelOrderPortal.close();
      },
      onError: (error: any) => {
        errorToast(error);
      },
    },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const totalPages = Math.ceil(cancelingReasons.length / itemsPerPage);

  const paginatedReasons = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return cancelingReasons.slice(start, start + itemsPerPage);
  }, [cancelingReasons, currentPage]);

  return (
    <Portal portal={cancelOrderPortal} title={trans("cancelOrder")}>
      <Form
        onSubmit={submitCancelingOrder}
        className="text-dark-900 flex flex-col gap-6"
      >
        <div className="flex flex-col gap-4">
          <RadioGroup
            name="reason"
            required
            onChange={setSelectedCancelingReason}
          >
            <div className="flex flex-col gap-2">
              {paginatedReasons.map((reason) => (
                <div key={reason.id} className="flex items-center gap-2">
                  <RadioInput
                    defaultValue={String(reason.id)}
                    classes={{
                      label: "px-2 py-0",
                      wrapper: "p-0 border-none w-full",
                    }}
                  >
                    {reason.reason}
                  </RadioInput>
                </div>
              ))}
            </div>
          </RadioGroup>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="rounded-full border px-4 py-1 text-sm transition-all hover:bg-gray-100 disabled:opacity-50"
              >
                {trans("previous")}
              </Button>
              <span className="text-sm font-medium">
                {currentPage} / {totalPages}
              </span>
              <Button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="rounded-full border px-4 py-1 text-sm transition-all hover:bg-gray-100 disabled:opacity-50"
              >
                {trans("next")}
              </Button>
            </div>
          )}
        </div>
        <TextareaInput
          minrows={4}
          name="notes"
          label={trans("notes")}
          className="rounded-xl"
          placeholder={trans("notes")}
        />
        <div className="mt-6 flex flex-col items-center justify-between gap-4 lg:flex-row">
          <Button
            className="w-full flex-1 justify-center rounded-full border bg-[#DFDFDF] px-6 py-3 font-semibold hover:bg-[#d3d3d3]"
            onClick={cancelOrderPortal.close}
          >
            {trans("cancel")}
          </Button>
          <SubmitButton className="bg-primary-light flex-1 justify-center rounded-full border px-6 py-3 font-semibold">
            {trans("cancelOrder")}
          </SubmitButton>
        </div>
      </Form>
    </Portal>
  );
}
